import { Component, effect, inject, ChangeDetectionStrategy } from '@angular/core';
import { MatButtonModule } from '@angular/material/button';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { APP_PATHS, LOCATION_DETAIL_TABS_VIEW, XEMEX_OID } from '@core/constants';
import {
  DeviceResponseModel,
  getUserActivePartner,
  getUserFeaturePermissions,
  LocationModel,
  LocationUpdateModel,
  Partner,
  Service,
  ServiceResponseModel,
} from '@core/models';
import {
  canUseInstallationFeature,
  canViewInstallations,
} from '@core/helpers/installation-access.helper';
import { installerOidFor } from '@core/helpers/installer-oid.helper';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { LocationStore } from '@root/app/store/location.store';
import { MatSidenavModule } from '@angular/material/sidenav';
import { UserStore } from '@root/app/store/user.store';
import { AuthStore } from '@root/app/store/auth.store';
import { ServiceStore } from '@root/app/store/service.store';
import {
  LocationGeneralTabComponent,
  LocationLinkRequest,
} from '../../components/location-general-tab/location-general-tab.component';
import { PageHeaderComponent } from '@custom-components/layout/page-header/page-header.component';
import { PageBodyComponent } from '@custom-components/layout/page-body/page-body.component';
import { BreadcrumbItem } from '@custom-components/general/breadcrumb/breadcrumb.component';
import { RecentlyVisitedService } from '@core/services';

@Component({
  selector: 'xemex-location-detail',
  imports: [
    MatButtonModule,
    RouterModule,
    TranslateModule,
    MatSidenavModule,
    LocationGeneralTabComponent,
    PageHeaderComponent,
    PageBodyComponent,
  ],
  providers: [LocationStore, ServiceStore],
  changeDetection: ChangeDetectionStrategy.Eager,
  templateUrl: './location-detail.component.html',
})
export class LocationDetailComponent {
  readonly locationStore = inject(LocationStore);
  readonly userStore = inject(UserStore);
  readonly authStore = inject(AuthStore);
  readonly serviceStore = inject(ServiceStore);
  private readonly translate = inject(TranslateService);
  private readonly recentlyVisited = inject(RecentlyVisitedService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private recentlyVisitedRecordedLocationId: LocationModel['locationId'] | undefined;
  locationDetail!: LocationModel;
  devicesByLocationId: DeviceResponseModel | undefined;
  servicesByLocationId: ServiceResponseModel | undefined;

  APP_PATHS = APP_PATHS;
  selectedTab = 'general';

  readonly availableTabs = LOCATION_DETAIL_TABS_VIEW;

  onTabChange(route: string) {
    this.selectedTab = route;
  }
  isLoading = false;
  isLoadingDevices = false;
  isLoadingLocationServices = false;

  get locationAddress() {
    return [
      this.locationDetail?.locationStreet,
      this.locationDetail?.locationStreetNumber,
      this.locationDetail?.locationStreetNumberAddition,
    ]
      .filter(Boolean)
      .join(' ');
  }

  get locationSubtitle() {
    return [
      this.locationDetail?.locationPostalCode,
      this.locationDetail?.locationCity,
      this.locationDetail?.locationCountry,
    ]
      .filter(Boolean)
      .join(', ');
  }

  get breadcrumbs(): BreadcrumbItem[] {
    return [
      { label: this.translate.instant('sidebarMenu.home'), routerLink: '/' },
      {
        label: this.translate.instant('sidebarMenu.locations'),
        routerLink: '/' + APP_PATHS.Locations,
      },
      { label: this.locationAddress || '—' },
    ];
  }

  constructor() {
    this.route.params.subscribe(params => {
      if (params['locationId']) {
        const locationId = params['locationId'];
        this.locationStore.getLocationDetailMethod(locationId);
        this.locationStore.getDevicesByLocationIdMethod(locationId);
        if (this.permissions().includes('view_services')) {
          this.serviceStore.getServicesByLocationId(locationId);
        }
      }
    });

    effect(() => {
      this.locationDetail = this.locationStore.locationDetail();
      this.devicesByLocationId = this.locationStore.devicesByLocationId();
      this.isLoading = this.locationStore.isLoading();
      this.isLoadingDevices = this.locationStore.isLoadingLocationDevices();
      this.isLoadingLocationServices = this.serviceStore.isLoadingLocationServices();
      this.servicesByLocationId = this.serviceStore.servicesByLocationId();
      if (
        this.locationDetail &&
        this.recentlyVisitedRecordedLocationId !== this.locationDetail.locationId
      ) {
        this.recentlyVisitedRecordedLocationId = this.locationDetail.locationId;
        this.recentlyVisited.record({
          type: 'location',
          id: `${this.locationDetail.locationId}`,
          icon: 'home_work',
          primary: this.locationAddress,
          secondary: this.locationSubtitle,
          route: ['/', APP_PATHS.Locations, `${this.locationDetail.locationId}`],
        });
      }
    });
  }

  onViewPartner(partnerOid: string) {
    this.router.navigate(['/', APP_PATHS.Partners, partnerOid]);
  }

  getActivePartner(): Partner | undefined {
    return getUserActivePartner(this.userStore.user());
  }

  permissions(): string[] {
    return getUserFeaturePermissions(this.userStore.user(), this.authStore.permissions(), XEMEX_OID);
  }

  showInstallations(): boolean {
    return canViewInstallations(this.userStore.user(), this.authStore.permissions());
  }

  canCreateInstallation(): boolean {
    return canUseInstallationFeature(
      this.userStore.user(),
      this.authStore.permissions(),
      'create_installation',
    );
  }

  onViewDevice(deviceId: string) {
    this.router.navigate(['/', APP_PATHS.Devices, deviceId]);
  }

  onLinkDevice({ deviceId, installationOid, choiceShown }: LocationLinkRequest) {
    const installerOid = installerOidFor(this.userStore.user(), this.locationDetail.partnerOid);
    if (!installerOid) {
      return;
    }
    this.locationStore.provisionDeviceMethod({
      deviceId,
      locationId: this.locationDetail.locationId,
      installerOid,
      ...(installationOid ? { installationOid } : {}),
      explainResult: choiceShown,
    });
  }

  updateLocation(location: LocationUpdateModel) {
    this.locationStore.updateLocationMethod({
      locationId: this.locationDetail.locationId,
      location: location,
    });
  }

  reloadLocation() {
    this.locationStore.getLocationDetailMethod(String(this.locationDetail.locationId));
  }

  updateService(_service: Service) {
    this.serviceStore.getServicesByLocationId(this.locationDetail.locationId);
  }
}
